import { LitElement, TemplateResult, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import innerStyles from './outline-cta.inner.pcss?inline';
import globalStyles from './outline-cta.global.pcss?inline';
import { AdoptedStyleSheets } from '../../controllers/adopted-stylesheets';

export type sizeType = 'small' | 'medium' | 'large';
export type fullWidthDevice = 'mobile' | 'desktop' | 'all' | undefined;
export type CTAVariants =
  | 'button-primary'
  | 'button-secondary'
  | 'button-tertiary'
  | 'link-primary'
  | 'link-secondary'
  | 'chip-primary';

/**
 * The OutlineCTA component
 * @element outline-cta
 *
 * @extends LitElement
 *
 * @slot - The CTA's main content.
 *
 * @property {sizeType} size - The size of the CTA.
 * @property {variantType} variant - The variant of the CTA.
 * @property {boolean} only-icon - Set to true only to render an icon.
 * @property {String} css-icon - Set to render an icon using css.
 *
 * Styles for this component are run directly off of attributes. Classes are
 * utilized for exports, such as in the <outline-ckeditor> component.
 *
 * The expectations is that this component consumes icons in its default slot,
 * either an <svg> or <outline-icon> component. It does not generate icons
 * within it's shadow root.
 *
 */
@customElement('outline-cta')
export class OutlineCTA extends LitElement {
  adoptedStyleSheets = new AdoptedStyleSheets(this, {
    globalCSS: globalStyles,
    encapsulatedCSS: innerStyles,
  });

  @property({ reflect: true })
  size: sizeType = 'medium';

  @property({ reflect: true })
  variant: CTAVariants = 'button-primary';

  @property({ reflect: true, type: String, attribute: 'full-width' })
  fullWidth: fullWidthDevice = undefined;

  @property({ reflect: true, type: Boolean, attribute: 'dark' })
  dark = false;

  @property({ reflect: true, type: Boolean, attribute: 'only-icon' })
  onlyIcon = false;

  @property({ reflect: false, type: String, attribute: 'css-icon' })
  cssIcon = '';

  render(): TemplateResult {
    return html`<slot></slot>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'outline-cta': OutlineCTA;
  }
}
